import { AcademicYear } from '@k12/models';

export const CURRENT_SCHOOL_YEAR_TOKEN = '{currentSchoolYear}';
export const NEXT_SCHOOL_YEAR_TOKEN = '{nextSchoolYear}';

export interface SchoolYearLabels {
  current: string;
  next: string;
}

const toUtc = (value: string): Date => new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`);

const toLabel = (year: AcademicYear): string =>
  `${toUtc(year.startDate).getUTCFullYear()}-${toUtc(year.endDate).getUTCFullYear()}`;

function currentAndNext(years: AcademicYear[], now: Date): [AcademicYear, AcademicYear] | null {
  const sorted = [...years].sort((a, b) => toUtc(a.startDate).getTime() - toUtc(b.startDate).getTime());
  const currentIndex = sorted.filter((year) => toUtc(year.startDate) <= now).length - 1;
  if (currentIndex < 0 || currentIndex + 1 >= sorted.length) {
    return null;
  }

  return [sorted[currentIndex], sorted[currentIndex + 1]];
}

export function getNextAcademicYear(years: AcademicYear[], now: Date = new Date()): AcademicYear | null {
  return currentAndNext(years, now)?.[1] ?? null;
}

export function hasSchoolYearTokens(value: unknown): boolean {
  const json = JSON.stringify(value ?? null);
  return json.includes(CURRENT_SCHOOL_YEAR_TOKEN) || json.includes(NEXT_SCHOOL_YEAR_TOKEN);
}

export function toSchoolYearLabels(years: AcademicYear[], now: Date = new Date()): SchoolYearLabels | null {
  const pair = currentAndNext(years, now);
  return pair ? { current: toLabel(pair[0]), next: toLabel(pair[1]) } : null;
}

export function resolveSchoolYearTokens<T>(value: T, labels: SchoolYearLabels | null): T {
  if (!labels || value == null || !hasSchoolYearTokens(value)) {
    return value;
  }

  const json = JSON.stringify(value);
  return JSON.parse(
    json.replaceAll(CURRENT_SCHOOL_YEAR_TOKEN, labels.current).replaceAll(NEXT_SCHOOL_YEAR_TOKEN, labels.next)
  ) as T;
}
